import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Sidebar from '../Sidebar';
import { Session } from "../utils/session";


const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};
export default function MembersEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    UserId: id,
    UserName: '',
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNo: '',
    UserRoleId: '',
    CompaniesId: '',
    IsActive: true,
    AvatarBase64: ''
  });


  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar(s => !s);
    } else {
      setHideDesktop(s => !s);
    }
  };

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) setShowMobileSidebar(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Sidebar logic - useEffect for direct DOM manipulation
  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
    if (state?.user) {
      setForm(f => ({
        ...f,
        ...{
          UserId: state.user.UserId,
          UserName: state.user.UserName || '',
          FirstName: state.user.FirstName || '',
          LastName: state.user.LastName || '',
          Email: state.user.Email || '',
          PhoneNo: state.user.PhoneNo || '',
          UserRoleId: state.user.UserRoleId || '',
          CompaniesId: state.user.CompaniesId || '',
          IsActive: state.user.IsActive ?? true,
          AvatarBase64: state.user.Avatar || ''
        }
      }));
    }
  }, [state]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        setLoading(true);

        const [rolesRes, companiesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/UserRole/roles`, { headers: authHeaders() }),
          fetch(`${API_BASE_URL}/api/UserRole/companies`, { headers: authHeaders() })
        ]);

        if (!rolesRes.ok) {
          console.error('Roles fetch failed', rolesRes.status, await rolesRes.text());
          throw new Error(`Roles request failed with status ${rolesRes.status}`);
        }
        if (!companiesRes.ok) {
          console.error('Companies fetch failed', companiesRes.status, await companiesRes.text());
          throw new Error(`Companies request failed with status ${companiesRes.status}`);
        }

        const rolesData = await rolesRes.json();
        const companiesData = await companiesRes.json();

        setRoles(rolesData || []);
        setCompanies(companiesData || []);
      } catch (err) {
        console.error('Failed to load lookups:', err);
      } finally {
        setLoading(false);
      }
    };

    loadLookups();
  }, []);


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({
        ...prev,
        AvatarBase64: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(`${API_BASE_URL}/api/Members/update`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });

      if (res.ok) {
        alert('Member updated successfully');
        navigate('/Members');
      } else {
        const errorText = await res.text();
        alert('Update failed: ' + errorText);
      }
    } catch (err) {
      alert('Update failed: ' + err.message);
    }
  };

  if (loading) return <div className="p-3">Loading...</div>;

 return (
  <div>
    {/* Sidebar */}
    <div id="sidebar">
      <Sidebar />
    </div>

    {/* Main Content */}
    <div id="content" className="p-4">

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => navigate('/Members')}
          >
            <i className="fas fa-arrow-left me-2"></i> Back
          </button>
          <h4 className="mb-0">Edit Member</h4>
        </div>
      </div>

      {/* Card */}
      <div className="card shadow-sm border-0">
        <div className="card-body">

          <form onSubmit={handleSubmit} className="row g-4">

            {/* Username */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Username</label>
              <input
                className="form-control"
                name="UserName"
                value={form.UserName}
                onChange={handleChange}
              />
            </div>

            {/* First Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">First Name</label>
              <input
                className="form-control"
                name="FirstName"
                value={form.FirstName}
                onChange={handleChange}
              />
            </div>

            {/* Last Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Last Name</label>
              <input
                className="form-control"
                name="LastName"
                value={form.LastName}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Email</label>
              <input
                className="form-control"
                name="Email"
                value={form.Email}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Company</label>
              <select
                className="form-select"
                name="CompaniesId"
                value={form.CompaniesId}
                onChange={handleChange}
              >
                <option value="">Select Company</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Role */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">Role</label>
              <select
                className="form-select"
                name="UserRoleId"
                value={form.UserRoleId}
                onChange={handleChange}
              >
                <option value="">Select Role</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Phone</label>
              <input
                className="form-control"
                name="PhoneNo"
                value={form.PhoneNo}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="form-check mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="IsActive"
                  checked={form.IsActive}
                  onChange={handleChange}
                />
                <label className="form-check-label fw-semibold">
                  Is Active
                </label>
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Profile Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImage}
              />
            </div>

            {/* Image Preview */}
            <div className="col-md-6 text-center">
              <label className="form-label fw-semibold d-block">
                Current Image
              </label>
              <img
                src={
                  form.AvatarBase64
                    ? form.AvatarBase64
                    : `/images/${form.UserName}.png`
                }
                alt="Avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://i.pravatar.cc/100?u=${form.UserId}`;
                }}
                className="rounded-circle shadow"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: 'cover',
                  border: '3px solid #f1f1f1'
                }}
              />
            </div>

            {/* Buttons */}
            <div className="col-12 text-end mt-4">
              <button type="submit" className="btn btn-success px-4">
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary ms-2 px-4"
                onClick={() => navigate('/Members')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

    </div>
  </div>
);

}
